import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { intl } from 'lang/intl'

import {
  IconComponent,
  WebinarHtmlDescriptionComponent as WebinarHtmlDescription,
} from '..'

import StatusLabel from './StatusLabelComponent'
import SpeakerRow from './SpeakerRowComponent'

/**
 * Container
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
  width: 100%;
`

/**
 * Title
 */
const Title = styled.h1`
  font-size: 18px;
  margin: 0px;
  margin-bottom: 4px;
  margin-top: 4px;
`

/**
 * SubTitle
 */
const SubTitle = styled.h2`
  color: #ababab;
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 1px;
  margin-bottom: 4px;
  margin-top: 4px;
  text-transform: uppercase;
`
/**
 * HtmlDescriptionContainer
 */
const HtmlDescriptionContainer = styled.div`
  display: flex;
  flex: 1;
  overflow-y: scroll;
  margin-bottom: 4px;
  margin-top: 4px;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar:vertical {
    width: 10px;
  }

  &::-webkit-scrollbar:horizontal {
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 5px;
    border: 2px solid white;
    background-color: rgba(0, 0, 0, 0.3);
  }
`

/**
 * DraftStatusIcon
 */
const DraftStatusIcon = () => (
  <IconComponent name="draft-icon" size={[12, 12]} color="f48630" />
)

/**
 * StatusLabelContainer
 */
const StatusLabelContainer = styled.div`
  margin-bottom: 5px;
`

/**
 * DraftStatusLabel
 */
const DraftStatusLabel = () => {
  return (
    <StatusLabelContainer>
      <StatusLabel
        backgroundColor="#fde2ce"
        description={intl.formatMessage({
          defaultMessage: 'In draft mode, you can test the room.',
        })}
        icon={<DraftStatusIcon />}
        title={intl.formatMessage({
          defaultMessage: 'Draft',
        })}
        titleColor="#f48630"
      />
    </StatusLabelContainer>
  )
}

/**
 * LiveStatusIcon
 */
const LiveStatusIcon = styled.div`
  background-color: #29c95b;
  border-radius: 50%;
  height: 6px;
  width: 6px;
`

/**
 * LiveStatusLabel
 */
const LiveStatusLabel = () => {
  return (
    <StatusLabelContainer>
      <StatusLabel
        backgroundColor="#eefbf3"
        description={intl.formatMessage({
          defaultMessage: 'You will be joining a live webinar.',
        })}
        icon={<LiveStatusIcon />}
        title={intl.formatMessage({
          defaultMessage: 'Live',
        })}
        titleColor="#29c95b"
      />
    </StatusLabelContainer>
  )
}

/**
 * SpeakerList
 */
const SpeakerList = ({ speakers, canDisableSpeakers }) =>
  speakers.map((speaker) => {
    return (
      <SpeakerRow
        key={speaker.live_speaker_id}
        name={speaker.name}
        title={speaker.title}
        link={speaker.link}
        linkedin={speaker.linkedin}
        facebook={speaker.facebook}
        twitter={speaker.twitter}
        company={speaker.company}
        avatarUrl={speaker.avatar_url}
        isHost={speaker.connected_role === 'producer'}
        disabled={canDisableSpeakers ? !speaker.display_as_connected_p : false}
      />
    )
  })

/**
 * WebinarInfoPaneComponent
 */
const WebinarInfoPaneComponent = ({
  title,
  description,
  status,
  speakers,
  canDisableSpeakers = false,
}) => (
  <Container>
    {status === 'draft' && <DraftStatusLabel />}
    {status === 'live' && <LiveStatusLabel />}
    <Title>{title}</Title>
    <HtmlDescriptionContainer>
      <WebinarHtmlDescription html={description} />
    </HtmlDescriptionContainer>
    {speakers.length > 0 && (
      <>
        <SubTitle>
          <FormattedMessage defaultMessage="Speakers" />
        </SubTitle>
        <SpeakerList
          speakers={speakers}
          canDisableSpeakers={canDisableSpeakers}
        />
      </>
    )}
  </Container>
)

WebinarInfoPaneComponent.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['draft', 'upcoming', 'live']).isRequired,
  speakers: PropTypes.arrayOf(PropTypes.object),
  canDisableSpeakers: PropTypes.bool,
}

export default WebinarInfoPaneComponent
